<template>
  <div class="container-fluid pt-4 px-4">

    <!-- Data freshness. Shown first, and deliberately: a backtest run against
         stale candles looks exactly like one run against current data. -->
    <v-card flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-text class="px-5 py-4 d-flex align-center flex-wrap ga-4">
        <div>
          <div class="text-caption text-medium-emphasis">Candle data through</div>
          <div class="text-h6 font-weight-medium">{{ freshness.latestDate || '—' }}</div>
        </div>
        <div>
          <div class="text-caption text-medium-emphasis">Stocks with candles that day</div>
          <div class="text-h6 font-weight-medium">{{ freshness.codesWithAnyCandles || 0 }}</div>
        </div>
        <!-- Captured and usable are different numbers: the runner skips any day
             below the full-session bar, so a day can be captured for 180 stocks
             and usable for far fewer. Backtesting against the first number
             while the second is what actually runs is how a thin result looks
             like a broad one. -->
        <div>
          <div class="text-caption text-medium-emphasis">
            Usable (&ge;{{ freshness.minCandlesForFullDay || 300 }} candles)
          </div>
          <div class="text-h6 font-weight-medium"
               :class="usableShortfall ? 'text-warning' : ''">
            {{ freshness.codesWithFullSession || 0 }}
          </div>
        </div>
        <v-chip v-if="freshnessWarning" size="small" variant="tonal" color="warning">
          {{ freshnessWarning }}
        </v-chip>
      </v-card-text>
    </v-card>

    <!-- Run a backtest -->
    <v-card flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-title class="px-5 py-4">
        <span class="text-h6 font-weight-medium">Run a backtest</span>
      </v-card-title>
      <v-card-text class="px-5 pb-5">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.strategyType" :items="strategyOptions" item-title="title" item-value="value"
              label="Strategy" variant="outlined" density="comfortable" hide-details />
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field v-model="form.fromDate" label="From (YYYY-MM-DD)" variant="outlined" density="comfortable" hide-details />
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field v-model="form.toDate" label="To (YYYY-MM-DD)" variant="outlined" density="comfortable" hide-details />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn color="primary" :loading="starting" :disabled="!form.strategyType" block @click="startRun">
              Run
            </v-btn>
          </v-col>
        </v-row>
        <div v-if="startError" class="text-caption text-error mt-2">{{ startError }}</div>
      </v-card-text>
    </v-card>

    <!-- Progress. An interrupted run shows a STOPPED bar: a bar still
         animating for a process that no longer exists is worse than none,
         because it manufactures confidence that work is happening. -->
    <v-card v-if="run && (run.presentedStatus === 'running' || run.presentedStatus === 'interrupted')"
            flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-text class="px-5 py-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <span class="text-subtitle-1">
            {{ run.presentedStatus === 'interrupted' ? 'Interrupted' : 'Running' }} — {{ run.strategy_type }}
          </span>
          <span class="text-caption text-medium-emphasis">
            {{ run.done_units || 0 }} / {{ run.total_units || '?' }}
            <template v-if="run.current_code"> · {{ run.current_code }}</template>
          </span>
        </div>
        <v-progress-linear
          :model-value="progressPct"
          :indeterminate="false"
          :color="run.presentedStatus === 'interrupted' ? 'warning' : 'primary'"
          height="18" rounded />
        <div v-if="run.presentedStatus === 'interrupted'" class="text-caption text-warning mt-2">
          The process appears to have stopped — no progress for a while. Nothing was corrupted;
          start a new run when ready.
        </div>
      </v-card-text>
    </v-card>

    <!-- Verdict -->
    <v-card v-if="verdict" flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-text class="px-5 py-4">
        <div class="d-flex align-center ga-3 mb-2">
          <v-chip size="large" variant="tonal" :color="verdictColor">{{ verdictLabel }}</v-chip>
          <span class="text-subtitle-1">{{ run.strategy_type }}</span>
        </div>
        <p class="text-body-2 mb-3">{{ verdict.reason }}</p>
        <v-row dense>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Cost hurdle</div>
            <div class="text-h6">{{ fmtR(verdict.hurdleR) }}</div>
            <div class="text-caption text-medium-emphasis">
              {{ verdict.roundTripCostPct }}% cost ÷ {{ verdict.stopBufferPct }}% stop
            </div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">95% interval (by day)</div>
            <div class="text-h6" v-if="verdict.interval">
              {{ fmtR(verdict.interval.lower) }} … {{ fmtR(verdict.interval.upper) }}
            </div>
            <div class="text-h6" v-else>—</div>
          </v-col>
          <!-- Trades AND trading days, always together. 213 trades across 22
               days must never read like 213 independent observations. -->
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Trades</div>
            <div class="text-h6">{{ verdict.tradeCount }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption text-medium-emphasis">Trading days</div>
            <div class="text-h6" :class="verdict.tradingDays < verdict.minTradingDays ? 'text-warning' : ''">
              {{ verdict.tradingDays }} <span class="text-caption">/ {{ verdict.minTradingDays }} needed</span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Headline stats -->
    <v-card v-if="overall && overall.tradeCount" flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-text class="px-5 py-4">
        <v-row dense>
          <v-col v-for="tile in statTiles" :key="tile.label" cols="6" md="2">
            <div class="text-caption text-medium-emphasis">{{ tile.label }}</div>
            <div class="text-h6" :class="tile.cls">{{ tile.value }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <EquityCurveChart v-if="dailyForChart.length" :daily="dailyForChart" title="Cumulative P&L by day" class="mb-4" />

    <!-- Per-day results -->
    <v-card v-if="days.length" flat rounded="lg" class="gap-backtest-card mb-4">
      <v-card-title class="px-5 py-4 d-flex align-center justify-space-between">
        <span class="text-h6 font-weight-medium">Per-day results</span>
        <v-btn v-if="run && run.status === 'completed'" size="small" variant="tonal"
               :loading="reviewing" @click="markReviewed">
          {{ run.reviewed_at ? 'Reviewed' : 'Mark reviewed' }}
        </v-btn>
      </v-card-title>
      <v-card-text class="px-5 pb-5">
        <div v-if="reviewMessage" class="text-caption text-success mb-2">{{ reviewMessage }}</div>
        <div class="table-scroll">
          <v-table density="compact" class="custom-data-table">
            <thead>
              <tr>
                <th>Date</th><th class="text-right">Trades</th><th class="text-right">W/L</th>
                <th class="text-right">Win %</th><th class="text-right">Expectancy</th>
                <th class="text-right">Exp (R)</th><th class="text-right">Charges</th><th class="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in days" :key="d.trade_date">
                <td>{{ d.trade_date }}</td>
                <td class="text-right">{{ d.trade_count }}</td>
                <td class="text-right">{{ d.win_count }}/{{ d.loss_count }}</td>
                <td class="text-right">{{ formatPct(d.win_rate_pct) }}</td>
                <td class="text-right">{{ formatMoney(d.expectancy_rs) }}</td>
                <td class="text-right">{{ fmtR(d.expectancy_r) }}</td>
                <td class="text-right">{{ formatMoney(d.charges_rs) }}</td>
                <td class="text-right" :class="deltaClass(d.net_pnl_rs)">{{ formatMoney(d.net_pnl_rs) }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Run history. This is also what makes a newly-added strategy visible
         here first: it lists every run, so a strategy with none stands out. -->
    <v-card flat rounded="lg" class="gap-backtest-card">
      <v-card-title class="px-5 py-4">
        <span class="text-h6 font-weight-medium">Recent runs</span>
      </v-card-title>
      <v-card-text class="px-5 pb-5">
        <div class="table-scroll">
          <v-table density="compact" class="custom-data-table">
            <thead>
              <tr>
                <th>#</th><th>Strategy</th><th>Range</th><th>Status</th>
                <th class="text-right">Trades</th><th>Reviewed</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in runs" :key="r.strategy_lab_run_id">
                <td>{{ r.strategy_lab_run_id }}</td>
                <td>{{ r.strategy_type }}</td>
                <td class="text-caption">{{ r.from_date }} → {{ r.to_date }}</td>
                <td>
                  <v-chip size="x-small" variant="tonal" :color="statusColor(r.presentedStatus)">
                    {{ r.presentedStatus }}
                  </v-chip>
                </td>
                <td class="text-right">{{ r.overall_stats ? r.overall_stats.tradeCount : '—' }}</td>
                <td class="text-caption">{{ r.reviewed_at ? 'yes' : '—' }}</td>
                <td class="text-right">
                  <v-btn size="x-small" variant="text" @click="viewRun(r.strategy_lab_run_id)">View</v-btn>
                </td>
              </tr>
              <tr v-if="!runs.length">
                <td colspan="7" class="py-6 text-center text-medium-emphasis">
                  No runs yet. Pick a strategy above and run one.
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-card-text>
    </v-card>

  </div>
</template>

<script>
// Strategy Lab -- backtest a strategy before anyone enables it for live
// trading. A strategy appears here first; enabling it stays a separate,
// deliberate step on Strategy Config. Nothing on this page starts trading.
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import EquityCurveChart from './EquityCurveChart.vue';

export default {
  name: 'StrategyLab',
  components: { EquityCurveChart },
  data() {
    return {
      form: { strategyType: null, fromDate: '', toDate: '' },
      strategies: [],
      freshness: {},
      runs: [],
      run: null,
      verdict: null,
      overall: null,
      days: [],
      starting: false,
      reviewing: false,
      startError: null,
      reviewMessage: null,
      pollTimer: null,
    };
  },
  computed: {
    // Only implemented strategies. getStrategy() throws for a registered-but-
    // unimplemented type, so offering it would just produce a failed run.
    strategyOptions() {
      return this.strategies
        .filter((s) => s.implemented)
        .map((s) => ({ title: s.strategyType, value: s.strategyType }));
    },
    progressPct() {
      const total = Number(this.run && this.run.total_units);
      const done = Number(this.run && this.run.done_units);
      if (!Number.isFinite(total) || total <= 0) return 0;
      return Math.round((done / total) * 100);
    },
    verdictLabel() {
      return this.verdict ? this.verdict.verdict.replace(/_/g, ' ') : '';
    },
    verdictColor() {
      return { PASS: 'success', FAIL: 'error', NOT_ENOUGH_DATA: 'warning' }[this.verdict && this.verdict.verdict] || 'grey';
    },
    dailyForChart() {
      // EquityCurveChart takes { period, pnlRs } and cumulatively sums.
      return this.days.map((d) => ({ period: d.trade_date, pnlRs: Number(d.net_pnl_rs) || 0 }));
    },
    statTiles() {
      const o = this.overall || {};
      return [
        { label: 'Trades', value: o.tradeCount ?? '—' },
        { label: 'Win rate', value: this.formatPct(o.winRatePct) },
        { label: 'Expectancy', value: this.formatMoney(o.expectancyRs), cls: this.deltaClass(o.expectancyRs) },
        { label: 'Profit factor', value: this.isFiniteNumber(o.profitFactor) ? Number(o.profitFactor).toFixed(2) : '—' },
        { label: 'Net P&L', value: this.formatMoney(o.netPnlRs), cls: this.deltaClass(o.netPnlRs) },
        { label: 'Max drawdown', value: this.formatMoney(o.maxDrawdownRs), cls: 'text-error' },
      ];
    },
    // A day captured for many stocks but complete for few is the quiet failure
    // mode: the run silently skips the incomplete ones, so the sample is far
    // narrower than the capture count suggests.
    usableShortfall() {
      const any = Number(this.freshness.codesWithAnyCandles) || 0;
      const full = Number(this.freshness.codesWithFullSession) || 0;
      return any > 0 && full < any * 0.9;
    },
    freshnessWarning() {
      if (!this.freshness.latestDate) return 'No candle data stored yet.';
      const days = Math.floor((Date.now() - new Date(`${this.freshness.latestDate}T00:00:00`).getTime()) / 86400000);
      if (days > 4) return `Last captured ${days} days ago — results may not reflect recent sessions.`;
      if (this.usableShortfall) {
        return `Only ${this.freshness.codesWithFullSession} of ${this.freshness.codesWithAnyCandles} stocks have a complete session — the rest are skipped.`;
      }
      return null;
    },
  },
  created() {
    this.loadStrategies();
    this.loadFreshness();
    this.loadRuns();
  },
  beforeUnmount() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
  methods: {
    // fetchWrapper rejects with a string or a status number, never an Error --
    // hence this idiom, used throughout the gap-backtest views.
    errText(err, fallback) {
      return typeof err === 'string' ? err : fallback;
    },
    isFiniteNumber(v) { return v !== null && v !== undefined && Number.isFinite(Number(v)); },
    formatPct(v) { return this.isFiniteNumber(v) ? `${Number(v).toFixed(1)}%` : '—'; },
    formatMoney(v) { return this.isFiniteNumber(v) ? this.$helper.formatPrice(Number(v)) : '—'; },
    fmtR(v) { return this.isFiniteNumber(v) ? `${Number(v).toFixed(3)} R` : '—'; },
    deltaClass(v) {
      if (!this.isFiniteNumber(v)) return '';
      return Number(v) >= 0 ? 'text-success' : 'text-error';
    },
    statusColor(s) {
      return { running: 'info', completed: 'success', failed: 'error', interrupted: 'warning' }[s] || 'grey';
    },

    loadStrategies() {
      fetchWrapper.get('api/strategy-settings')
        .then((res) => { this.strategies = res?.data || []; })
        .catch(() => { this.strategies = []; });
    },
    loadFreshness() {
      fetchWrapper.get('api/strategy-lab/data-freshness')
        .then((res) => { this.freshness = res?.data || {}; })
        .catch(() => { this.freshness = {}; });
    },
    loadRuns() {
      fetchWrapper.get('api/strategy-lab/runs')
        .then((res) => { this.runs = res?.data || []; })
        .catch(() => { this.runs = []; });
    },

    startRun() {
      this.starting = true;
      this.startError = null;
      this.reviewMessage = null;
      fetchWrapper.post('api/strategy-lab/runs', {
        strategyType: this.form.strategyType,
        fromDate: this.form.fromDate,
        toDate: this.form.toDate,
      }).then((res) => {
        this.starting = false;
        const runId = res?.data?.runId;
        if (runId) this.viewRun(runId);
      }).catch((err) => {
        this.starting = false;
        this.startError = this.errText(err, 'Could not start the run.');
      });
    },

    // silent: a background poll must never replace the view with an error
    // banner every few seconds (the pattern ParamOptimizer.vue uses).
    viewRun(runId, { silent = false } = {}) {
      // Clear any in-flight poll first. Without this, clicking Run or View
      // while a poll chain is alive starts a SECOND chain -- both then write
      // this.run every few seconds, and beforeUnmount only ever clears the last
      // timer set, so the other keeps firing after the page is gone.
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.pollTimer = null;

      fetchWrapper.get(`api/strategy-lab/runs/${encodeURIComponent(runId)}`)
        .then((res) => {
          const data = res?.data;
          if (!data) return;
          this.run = data;
          this.verdict = data.verdict;
          this.overall = data.overall_stats;

          if (data.presentedStatus === 'running') {
            // Deliberately NOT loading per-day rows here: they are written in
            // one go when the run completes, so polling them every 3s for
            // several minutes is a request per poll that can only return [].
            this.pollTimer = setTimeout(() => this.viewRun(runId, { silent: true }), 3000);
          } else {
            this.loadDays(runId);
            this.loadRuns();
          }
        })
        .catch((err) => {
          if (!silent) this.startError = this.errText(err, 'Could not load the run.');
          this.pollTimer = setTimeout(() => this.viewRun(runId, { silent: true }), 5000);
        });
    },

    loadDays(runId) {
      fetchWrapper.get(`api/strategy-lab/runs/${encodeURIComponent(runId)}/days`)
        .then((res) => { this.days = res?.data || []; })
        .catch(() => { this.days = []; });
    },

    markReviewed() {
      if (!this.run) return;
      this.reviewing = true;
      fetchWrapper.post(`api/strategy-lab/runs/${encodeURIComponent(this.run.strategy_lab_run_id)}/review`, {})
        .then((res) => {
          this.reviewing = false;
          this.reviewMessage = res?.message || 'Marked reviewed.';
          this.viewRun(this.run.strategy_lab_run_id, { silent: true });
        })
        .catch((err) => {
          this.reviewing = false;
          this.startError = this.errText(err, 'Could not mark it reviewed.');
        });
    },
  },
};
</script>
